import React, {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import { isYoutubeReconnectError } from '../utils/youtubeBinaryUpload';
import {
  fetchYoutubeAuthState,
  waitForYoutubeAuthReady,
  shouldShowYoutubeOAuthButton,
  youtubeOAuthButtonColor,
  youtubeOAuthButtonLabel,
  youtubeOAuthHintLine,
} from '../utils/youtubeAuthState';
import {
  openYoutubeOAuthConnect,
  subscribeYoutubeOAuthComplete,
  installYoutubeOAuthReturnListener,
} from '../utils/youtubeOAuthFlow';

const YOUTUBE_LOGO_URI =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/YouTube_full-color_icon_%282017%29.svg/1280px-YouTube_full-color_icon_%282017%29.svg.png';

/**
 * Connect / Reconnect YouTube — popup OAuth on web (no full-page reload / logout).
 * @typedef {Object} YoutubeOAuthBarHandle
 * @property {() => Promise<import('../utils/youtubeAuthState').YoutubeAuthState|undefined>} refresh
 * @property {(message?: string) => void} markNeedsReconnect
 */

/**
 * @param {{ accountKey?: string, containerStyle?: object, variant?: 'default' | 'upload-break-modal' }} props
 * @param {React.Ref<YoutubeOAuthBarHandle>} ref
 */
function YoutubeOAuthBarInner(
  { accountKey = 'default', containerStyle, variant = 'default' },
  ref
) {
  const theme = useTheme();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const styles = getStyles(theme, variant);
  const isUploadBreakModal = variant === 'upload-break-modal';
  const webBoxProps =
    Platform.OS === 'web' && isUploadBreakModal
      ? { dataSet: { uploadBreakYoutubeOAuth: 'true' } }
      : {};
  const webTextProps =
    Platform.OS === 'web' && isUploadBreakModal
      ? { dataSet: { youtubeOauthText: 'true' } }
      : {};

  const refresh = useCallback(async () => {
    const state = await fetchYoutubeAuthState(accountKey);
    setStatus(state);
    return state;
  }, [accountKey]);

  const markNeedsReconnect = useCallback((message) => {
    setStatus((prev) => ({
      checked: true,
      tokenValid: false,
      hasStoredToken: true,
      needsReconnect: true,
      needsInitialConnect: false,
      showOAuthButton: true,
      invalidGrant: isYoutubeReconnectError(message),
      channelTitle: prev?.channelTitle ?? null,
    }));
  }, []);

  useImperativeHandle(ref, () => ({ refresh, markNeedsReconnect }), [
    refresh,
    markNeedsReconnect,
  ]);

  useEffect(() => {
    installYoutubeOAuthReturnListener();
    void refresh();
  }, [refresh]);

  const confirmConnectionOrExplain = useCallback(
    async (result) => {
      const next = await waitForYoutubeAuthReady(accountKey);
      setStatus(next);
      if (next.tokenValid) return next;
      if (result?.error && result.error !== 'popup_blocked') {
        Alert.alert('YouTube', String(result.error));
      } else {
        Alert.alert(
          'YouTube not connected',
          'Authorization finished but the server still cannot use YouTube uploads. Confirm GOOGLE_REDIRECT_URI on Render matches Google Cloud (…/oauth2callback), then try Reconnect again.'
        );
      }
      return next;
    },
    [accountKey]
  );

  useEffect(() => {
    return subscribeYoutubeOAuthComplete((result) => {
      if (result.success) {
        void confirmConnectionOrExplain(result);
        return;
      }
      if (result.closed || result.checkServer) {
        void confirmConnectionOrExplain(result);
        return;
      }
      if (result.error && result.error !== 'popup_blocked') {
        Alert.alert('YouTube', String(result.error));
        void refresh();
      }
    });
  }, [confirmConnectionOrExplain, refresh]);

  const startYoutubeOAuthConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const result = await openYoutubeOAuthConnect({ accountKey, forceConsent: true });
      if (result.success || result.closed || result.checkServer) {
        await confirmConnectionOrExplain(result);
      } else if (result.error && result.error !== 'popup_blocked') {
        Alert.alert('YouTube', String(result.error));
        await refresh();
      }
    } catch (e) {
      Alert.alert('YouTube', e?.message || 'Could not open YouTube authorization.');
    } finally {
      setConnecting(false);
    }
  }, [accountKey, confirmConnectionOrExplain, refresh]);

  if (status === null || !status.checked) {
    return (
      <View
        style={[styles.root, containerStyle]}
        accessibilityLabel="YouTube connection status loading"
        {...webBoxProps}
      >
        <ActivityIndicator color={theme.isDarkMode ? '#e0e0e0' : '#666'} />
      </View>
    );
  }

  if (shouldShowYoutubeOAuthButton(status)) {
    const hint = youtubeOAuthHintLine(status);
    const panelStyle = isUploadBreakModal
      ? [styles.root, styles.uploadBreakPanel, containerStyle]
      : [styles.root, containerStyle];
    return (
      <View style={panelStyle} {...webBoxProps}>
        {hint ? (
          <Text
            style={[
              styles.warningLine,
              isUploadBreakModal ? styles.textOnUploadBreakPanel : { color: theme.colors.text },
            ]}
            {...webTextProps}
          >
            {hint}
          </Text>
        ) : null}
        <TouchableOpacity
          style={[
            styles.btn,
            isUploadBreakModal && styles.btnInsideUploadBreakPanel,
            { backgroundColor: youtubeOAuthButtonColor(status) },
          ]}
          onPress={() => void startYoutubeOAuthConnect()}
          disabled={connecting}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={
            status.needsReconnect ? 'Reconnect YouTube account' : 'Connect YouTube account'
          }
        >
          {connecting ? (
            <ActivityIndicator color={theme.isDarkMode ? '#ffffff' : '#000000'} />
          ) : (
            <>
              <Image
                source={{ uri: YOUTUBE_LOGO_URI }}
                style={styles.logo}
                contentFit="contain"
                {...(Platform.OS !== 'web' ? { accessibilityIgnoresInvertColors: true } : {})}
              />
              <Text
                style={[
                  styles.btnText,
                  { color: theme.isDarkMode ? '#ffffff' : '#000000' },
                ]}
              >
                {youtubeOAuthButtonLabel(status)}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    );
  }

  if (status.tokenValid && status.channelTitle) {
    const panelStyle = isUploadBreakModal
      ? [styles.root, styles.uploadBreakPanel, containerStyle]
      : [styles.root, containerStyle];
    return (
      <View style={panelStyle} {...webBoxProps}>
        <Text
          style={[
            styles.channelLine,
            isUploadBreakModal ? styles.textOnUploadBreakPanel : { color: theme.colors.text },
          ]}
          {...webTextProps}
        >
          Connected: {status.channelTitle}
        </Text>
      </View>
    );
  }

  return null;
}

const YoutubeOAuthBar = forwardRef(YoutubeOAuthBarInner);
export default YoutubeOAuthBar;

const getStyles = (theme, variant) =>
  StyleSheet.create({
    root: {
      marginBottom: 10,
    },
    warningLine: {
      fontSize: 13,
      marginBottom: 8,
      lineHeight: 18,
      opacity: variant === 'upload-break-modal' ? 1 : 0.9,
    },
    uploadBreakPanel: {
      backgroundColor: '#000000',
      borderRadius: 5,
      padding: 15,
      overflow: 'hidden',
    },
    textOnUploadBreakPanel: {
      color: '#ffffff',
      marginBottom: 12,
    },
    btnInsideUploadBreakPanel: {
      marginTop: 4,
    },
    btn: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderRadius: 8,
      minHeight: 72,
    },
    logo: {
      width: 44,
      height: 44 * (70 / 99),
      marginBottom: 8,
    },
    btnText: {
      fontSize: 15,
      fontWeight: '700',
    },
    channelLine: {
      fontSize: 13,
      opacity: 0.85,
    },
  });
